import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSourcingView } from '../../utils/sidebarPreferences';
import '../Sidebar.css';

export default function SidebarNavigation({ companyName, collapsed, onToggle, sourcingOnly = false,
  onPostJob, onSignOut, onUpgrade, trial, showCommunityItem, showManpowerRequestsItem }) {
  const { t, i18n } = useTranslation('sidebar');
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(null);
  const isRTL = i18n.dir() === 'rtl';
  const isActive = path => location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
  const sourcingView = getSourcingView(location.search) || 'search';
  const tools = [
    ['/imported-cvs', 'fa-download', 'importedCVs'], ['/contracts', 'fa-file-contract', 'contracts'],
    ['/org-intelligence', 'fa-brain', 'orgIntelligence'], ['/data-analytics', 'fa-chart-pie', 'dataAnalytics'],
    ['/external-apps', 'fa-puzzle-piece', 'externalApps'],
  ];
  const hiringPaths = ['/', '/applicants', '/manpower-requests', '/employee-onboarding', '/my-team', '/payroll', '/community'];
  const activeTool = tools.some(([path]) => isActive(path)) || (sourcingOnly && hiringPaths.some(isActive));
  const showTools = !collapsed && (toolsOpen ?? activeTool);
  const navItem = (to, icon, key, active = isActive(to), onClick) => <Link key={key} to={to}
    className="workspace-nav-link" aria-current={active ? 'page' : undefined}
    aria-label={t(`navigation.${key}`)} title={collapsed ? t(`navigation.${key}`) : undefined} onClick={onClick}
    state={to === '/' ? { showHome: true } : undefined}>
    <i className={`fa-solid ${icon}`} aria-hidden="true" /><span className="workspace-nav-label">{t(`navigation.${key}`)}</span>
  </Link>;
  const action = (key, icon, onClick) => <button type="button" className="workspace-nav-link" onClick={onClick}
    aria-label={t(`navigation.${key}`)} title={collapsed ? t(`navigation.${key}`) : undefined}>
    <i className={`fa-solid ${icon}`} aria-hidden="true" /><span className="workspace-nav-label">{t(`navigation.${key}`)}</span>
  </button>;
  const hiringLinks = <>
    {action('postJob', 'fa-plus', onPostJob)}
    {navItem('/', 'fa-house', 'home')}
    {showManpowerRequestsItem && navItem('/manpower-requests', 'fa-clipboard-list', 'manpowerRequests')}
    {navItem('/applicants', 'fa-address-book', 'applicants')}
  </>;
  const teamLinks = <>
    {navItem('/employee-onboarding', 'fa-user-plus', 'onboarding')}
    {navItem('/my-team', 'fa-people-group', 'myTeam')}
    {showCommunityItem && navItem('/community', 'fa-users', 'community')}
  </>;
  const onSourcing = location.pathname.startsWith('/sourcing') || location.pathname === '/talent/profile';
  const sourcingActive = onSourcing || isActive('/outreach');
  const sourcingLinks = <>
    <div className="workspace-sourcing-parent" data-active={sourcingActive}>
      {navItem('/sourcing?view=search', 'fa-magnifying-glass', 'sourcing', false)}
    </div>
    {sourcingActive && <div className="workspace-sourcing-links is-nested">
      {navItem('/sourcing?view=search', 'fa-magnifying-glass', 'search', onSourcing && sourcingView !== 'shortlists')}
      {navItem('/sourcing?view=shortlists', 'fa-bookmark', 'shortlists', location.pathname === '/sourcing' && sourcingView === 'shortlists')}
      {navItem('/outreach', 'fa-envelope-open-text', 'outreach')}
    </div>}
  </>;
  return <aside className={`workspace-sidebar${collapsed ? ' is-collapsed' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} aria-label={t('navigation.mainNavigation')}>
    <div className="workspace-sidebar-header">
      <Link className="workspace-company sidebar-company-link" to="/profile" title={`${companyName || t('company.fallback')} — ${t('company.editSettingsHint')}`}>
        <span className="workspace-company-mark" aria-hidden="true">{(companyName || 'C').slice(0, 1)}</span>
        <span className="workspace-company-copy">
          <span className="workspace-company-name"><span className="workspace-company-name-text">{companyName || t('company.fallback')}</span><i className="fa-solid fa-chevron-down" aria-hidden="true" /></span>
          <span className="workspace-company-hint">{t('company.editSettingsHint')}</span>
        </span>
      </Link>
      <button className="workspace-sidebar-toggle" type="button" onClick={() => onToggle(!collapsed)}
        aria-label={t(collapsed ? 'tooltips.expand' : 'tooltips.collapse')} title={t(collapsed ? 'tooltips.expand' : 'tooltips.collapse')}>
        <i className={`fa-solid fa-circle-chevron-${isRTL ? 'right' : 'left'}`} aria-hidden="true" />
      </button>
    </div>
    <nav className="workspace-sidebar-scroll" aria-label={t('navigation.mainNavigation')}>
      <div className="workspace-nav-group">
        {!sourcingOnly && !collapsed && <p className="workspace-nav-heading">{t('navigation.hire')}</p>}
        {!sourcingOnly && hiringLinks}
        {sourcingLinks}
      </div>
      {!sourcingOnly && <div className="workspace-nav-group workspace-nav-group-separated">
        {!collapsed && <p className="workspace-nav-heading">{t('navigation.manageTeam')}</p>}
        {teamLinks}
      </div>}
      <div className="workspace-nav-group workspace-nav-group-separated">
        <button type="button" className="workspace-nav-link workspace-more" aria-expanded={showTools} aria-controls="workspace-more-tools"
          aria-label={t('navigation.moreTools')} title={collapsed ? t('navigation.moreTools') : undefined}
          onClick={() => { if (collapsed) onToggle(false); setToolsOpen(!showTools); }}>
          <i className="fa-solid fa-grip" aria-hidden="true" /><span className="workspace-nav-label">{t('navigation.moreTools')}</span>
          {!collapsed && <i className={`fa-solid fa-chevron-${showTools ? 'up' : 'down'} workspace-more-chevron`} aria-hidden="true" />}
        </button>
        <div id="workspace-more-tools" hidden={!showTools}>
          {sourcingOnly && <>
            <p className="workspace-nav-heading">{t('navigation.hire')}</p>
            {hiringLinks}
            <p className="workspace-nav-heading workspace-more-heading">{t('navigation.manageTeam')}</p>
            {teamLinks}
          </>}
          <div className={sourcingOnly ? 'workspace-nav-group workspace-nav-group-separated' : 'workspace-nav-group'}>
            {tools.map(([path, icon, key]) => navItem(path, icon, key))}
          </div>
        </div>
      </div>
    </nav>
    <div className="workspace-sidebar-footer">
      {trial && !collapsed && <div className="workspace-trial"><span><i className="fa-regular fa-clock" aria-hidden="true" />{t('trial.daysLeft', { days: trial.daysRemaining })}</span>
        <button type="button" onClick={onUpgrade}>{t('trial.upgradeNow')}</button></div>}
      {navItem('/profile', 'fa-gear', 'settings')}
      {action('signOut', 'fa-arrow-right-from-bracket', onSignOut)}
    </div>
  </aside>;
}
